import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { toast } from "sonner";
import { Loader2, Truck, Eye, Users, Radio, Clock, ChevronDown, ChevronUp, ArrowLeft } from "lucide-react";

export default function VendorAnalyticsPanel() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("sessions_this_month");
  const [sortDir, setSortDir] = useState("desc");
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/vendor-analytics`);
      setVendors(res.data.vendors || []);
    } catch { toast.error("Failed to load vendor analytics"); }
    finally { setLoading(false); }
  };

  const loadDetail = async (userId) => {
    if (expanded === userId) { setExpanded(null); setDetail(null); return; }
    setExpanded(userId);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/admin/vendor-analytics/${userId}`);
      setDetail(res.data);
    } catch { toast.error("Failed to load vendor detail"); }
    finally { setDetailLoading(false); }
  };

  const handleSort = (col) => {
    if (sortBy === col) { setSortDir(d => d === "asc" ? "desc" : "asc"); }
    else { setSortBy(col); setSortDir("desc"); }
  };

  const sorted = [...vendors].sort((a, b) => {
    const aVal = a[sortBy] ?? 0;
    const bVal = b[sortBy] ?? 0;
    return sortDir === "asc" ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
  });

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return null;
    return sortDir === "asc" ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />;
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="admin-vendor-analytics">
      <p className="text-xs text-slate-500 mb-3">{vendors.length} verified vendor{vendors.length !== 1 ? "s" : ""}</p>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-200 text-slate-400">
              <th className="text-left py-2 pr-2 font-medium">Vendor</th>
              <th className="text-right py-2 px-2 font-medium cursor-pointer hover:text-slate-600" onClick={() => handleSort("views_this_month")} data-testid="sort-views">
                Views <SortIcon col="views_this_month" />
              </th>
              <th className="text-right py-2 px-2 font-medium cursor-pointer hover:text-slate-600" onClick={() => handleSort("followers")} data-testid="sort-followers">
                Followers <SortIcon col="followers" />
              </th>
              <th className="text-right py-2 px-2 font-medium cursor-pointer hover:text-slate-600" onClick={() => handleSort("sessions_this_month")} data-testid="sort-sessions">
                Sessions <SortIcon col="sessions_this_month" />
              </th>
              <th className="text-right py-2 pl-2 font-medium cursor-pointer hover:text-slate-600" onClick={() => handleSort("days_remaining")} data-testid="sort-trial">
                Trial <SortIcon col="days_remaining" />
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(v => (
              <VendorRow key={v.user_id} v={v} expanded={expanded === v.user_id}
                onToggle={() => loadDetail(v.user_id)} detail={detail} detailLoading={detailLoading} />
            ))}
          </tbody>
        </table>
      </div>

      {vendors.length === 0 && (
        <p className="text-center text-slate-400 text-sm py-8">No verified vendors yet.</p>
      )}
    </div>
  );
}

function VendorRow({ v, expanded, onToggle, detail, detailLoading }) {
  const trialColor = v.trial_expired ? "text-red-500" : v.days_remaining <= 30 ? "text-amber-500" : "text-emerald-500";

  return (
    <>
      <tr className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition" onClick={onToggle} data-testid={`vendor-row-${v.user_id}`}>
        <td className="py-2.5 pr-2">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
              <Truck className="w-3.5 h-3.5 text-amber-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-700 text-xs">{v.truck_name}</p>
              <p className="text-[10px] text-slate-400">{v.city}</p>
            </div>
          </div>
        </td>
        <td className="text-right py-2.5 px-2 text-slate-600 font-medium">{v.views_this_month}</td>
        <td className="text-right py-2.5 px-2 text-slate-600 font-medium">{v.followers}</td>
        <td className="text-right py-2.5 px-2 text-slate-600 font-medium">{v.sessions_this_month}</td>
        <td className={`text-right py-2.5 pl-2 font-bold ${trialColor}`}>
          {v.trial_expired ? "Expired" : `${v.days_remaining}d`}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={5} className="bg-slate-50 px-3 py-3">
            {detailLoading ? (
              <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
            ) : detail ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <DetailStat label="Total Sessions" value={detail.live_sessions?.total_sessions} icon={Radio} />
                <DetailStat label="Avg Duration" value={`${detail.live_sessions?.avg_session_duration_minutes ?? 0}m`} icon={Clock} />
                <DetailStat label="Total Views" value={detail.profile?.total_views} icon={Eye} />
                <DetailStat label="Unique Customers" value={detail.punch_cards?.unique_customers} icon={Users} />
                <DetailStat label="Rewards Redeemed" value={detail.punch_cards?.total_redeemed} icon={Users} />
                <DetailStat label="Community Spots" value={detail.community?.total_spots} icon={Eye} />
                <DetailStat label="Unmet Demand" value={detail.discovery?.unmet_demand} icon={Eye} />
                <DetailStat label="Trial Days Left" value={detail.trial?.days_remaining} icon={Clock} />
              </div>
            ) : <p className="text-slate-400 text-center">No data</p>}
          </td>
        </tr>
      )}
    </>
  );
}

function DetailStat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white rounded-lg p-2.5 border border-slate-200">
      <Icon className="w-3 h-3 text-amber-500 mb-1" />
      <p className="text-sm font-bold text-slate-700">{value ?? "—"}</p>
      <p className="text-[10px] text-slate-400">{label}</p>
    </div>
  );
}
